// Static marketing sections (hero -> team), originally ported from the approved
// feat-153 mockup references/mockup/index.html. Purely presentational (no user
// data, no interactivity) so it is rendered via dangerouslySetInnerHTML to keep
// 1:1 visual fidelity with the taste-approved mockup and avoid lossy hand-porting
// of ~40 inline SVGs. The interactive nav, waitlist form, and footer live in
// Homepage.tsx as real JSX. Asset paths point at /marketing/assets (public/).
//
// feat-288-update-testing-language: the COPY here deliberately diverges from that
// mockup and the mockup is no longer the copy oracle — the shipped page is. The
// structure, layout, and SVGs are still 1:1. Changed: stage 4 and the measurement
// group dropped the split-test notation and now read plain "Test" / "testing"; the
// hero h1 and sub name guest experience alongside revenue; step 1 reads "Set a
// goal." with a matching "Goal" field in its mock form. Do not "restore fidelity"
// to references/mockup/index.html on any of those strings. The absence of that
// notation in this file is asserted by a grep in the feat-288 spec, so do not
// reintroduce it even inside a comment.

export const HOMEPAGE_STATIC_MARKUP = `

<!-- ============ HERO ============ -->
<section class="hero">
  <div class="wrap hero-grid">
    <div class="hero-copy">
      <span class="eyebrow">In-person commerce, starting with restaurants</span>
      <h1>Increase your revenue and improve guest experience. <span class="accent">Train, measure, and test your strategies.</span></h1>
      <p class="sub">Bespoke is the revenue and guest experience tool for the last ten feet, the live, in-person, face-to-face conversation between your team and your customers that no one has ever measured. Close the loop: set a strategy, gamify the training, measure live service, and test the result. Keep what lifts revenue and scale it. Built for in-person commerce everywhere, starting with restaurants.</p>
      <div class="hero-cta">
        <a href="#waitlist" class="btn btn-primary">Join the waitlist <span class="arrow">&rarr;</span></a>
        <a href="#loop" class="btn btn-ghost">See the loop</a>
      </div>
      <div class="hero-note">
        <span class="dot"></span>
        <span>Runs on the audio you already have and the point of sale you already use. No new headcount, nothing new to learn.</span>
      </div>
    </div>

    <div class="hero-visual">
      <div class="scorecard">
        <span class="tag">Shift Scorecard</span>
        <div class="sc-head">
          <div class="sc-avatar">M</div>
          <div class="sc-id">
            <div class="nm">Maya R.</div>
            <div class="mt">Dinner service &middot; Section 4</div>
          </div>
          <div class="sc-score">92<small>/100</small></div>
        </div>
        <div class="sc-rows">
          <div class="sc-row">
            <span class="lbl"><i class="b-green"></i> Warm greeting</span>
            <span class="bar"><span class="b-green" style="width:92%"></span></span>
          </div>
          <div class="sc-row">
            <span class="lbl"><i class="b-amber"></i> Featured upsell</span>
            <span class="bar"><span class="b-amber" style="width:58%"></span></span>
          </div>
          <div class="sc-row">
            <span class="lbl"><i class="b-red"></i> Dessert offer</span>
            <span class="bar"><span class="b-red" style="width:34%"></span></span>
          </div>
        </div>
        <div class="sc-foot">
          <span class="cap">Coached against tonight's strategy</span>
          <span class="rev">Review &rarr;</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ FLOOR PHOTO ============ -->
<section class="floor-photo">
  <div class="wrap">
    <figure class="floor-fig">
      <img src="/marketing/assets/img/server_hero.jpg" alt="A server taking an order from a guest in a city restaurant">
      <figcaption>
        <span class="fc-eyebrow">The last ten feet</span>
        <span class="fc-line">The live, in-person conversation where revenue is won or lost. Now, finally, measurable.</span>
      </figcaption>
    </figure>
  </div>
</section>

<!-- ============ BUILT FOR ============ -->
<section class="industries-band">
  <div class="wrap">
    <span class="ind-lab">Built for</span>
    <span class="ind-list"><b>Restaurants</b><svg class="ind-chk" viewBox="0 0 24 24" fill="none" stroke="#3F6328" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l5 5L20 6"/></svg> &middot; Retail &middot; Hotels &middot; Fitness &middot; Trades &middot; Automotive &middot; Tourism &middot; Pharma sales</span>
  </div>
</section>

<!-- ============ OPERATOR LOGOS ============ -->
<section class="operators">
  <div class="wrap">
    <p class="label">Built with operators who set the bar</p>
    <div class="op-row">
      <div class="op-chip"><img src="/marketing/assets/logos/op_lazybear.png" alt="Lazy Bear"></div>
      <div class="op-chip"><img src="/marketing/assets/logos/op_gen.png" alt="Gen Korean BBQ"></div>
      <div class="op-chip"><img src="/marketing/assets/logos/op_sajj.png" alt="Sajj Mediterranean"></div>
      <div class="op-chip"><img src="/marketing/assets/logos/op_canters.png" alt="Canter's"></div>
      <div class="op-chip"><img src="/marketing/assets/logos/op_horsefeather.png" alt="Horsefeather"></div>
      <div class="op-chip"><img src="/marketing/assets/logos/op_ladleleaf.png" alt="Ladle &amp; Leaf"></div>
      <div class="op-chip"><img src="/marketing/assets/logos/op_grove.png" alt="The Grove"></div>
    </div>
  </div>
</section>

<!-- ============ THE RESULT (feat-291 case study) ============ -->
<section class="result" id="case-study">
  <div class="wrap result-grid">

    <div class="result-copy">
      <span class="eyebrow">The result</span>
      <h2 class="section-title">Ladle &amp; Leaf, a multi-unit fast-casual chain, raises checks <span class="accent">4.3%</span> and tips <span class="accent">8.4%</span>.</h2>
      <p>A statistically rigorous test conducted by a third-party data analytics firm, using control and test stores and point-of-sale data, showed the impact of Bespoke.</p>
      <a class="btn btn-primary"
         href="/marketing/bespoke-case-study.pdf"
         target="_blank" rel="noopener noreferrer">Read the case study</a>
    </div>

    <div class="stat-card">
      <div class="stat-row">
        <span class="stat-fig">+4.3<span class="pct">%</span></span>
        <span class="stat-label">Average check</span>
      </div>
      <div class="stat-row">
        <span class="stat-fig">+3.1<span class="pct">%</span></span>
        <span class="stat-label">Items per transaction</span>
      </div>
      <div class="stat-row">
        <span class="stat-fig">+8.4<span class="pct">%</span></span>
        <span class="stat-label">Server tips</span>
      </div>
    </div>

  </div>
</section>

<!-- ============ THE LOOP ============ -->
<section class="loop" id="loop">
  <div class="wrap loop-grid">
    <div class="loop-copy">
      <span class="eyebrow">The loop</span>
      <h2>Strategy, training, measurement, and testing, <span class="accent">finally in one loop.</span></h2>
      <p>The digital side of commerce is measured to exhaustion. The last ten feet, the live conversation between a server and a guest, never has been. Bespoke captures it and turns it into something you can act on, again and again.</p>
    </div>

    <div class="loop-visual">
      <div class="diagram">
        <svg class="ring-svg" viewBox="0 0 440 440">
          <circle class="track" cx="220" cy="220" r="168"></circle>
          <circle class="arc" cx="220" cy="220" r="168"></circle>
        </svg>
        <div class="hub"><img src="/marketing/assets/logo_badge.png" alt="Bespoke"></div>

        <div class="node n1">
          <div class="pip"><span class="num">1</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="#D6453C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4h11l-2 4 2 4H5"/></svg>
          </div>
          <div class="nlabel">Strategy</div>
        </div>
        <div class="node n2">
          <div class="pip"><span class="num">2</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="#C68A2E" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l5 5L20 6"/></svg>
          </div>
          <div class="nlabel">Train</div>
        </div>
        <div class="node n3">
          <div class="pip"><span class="num">3</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="#4C7FB5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19V5M4 19h16M8 15l3-4 3 2 4-6"/></svg>
          </div>
          <div class="nlabel">Measure</div>
        </div>
        <div class="node n4">
          <div class="pip"><span class="num">4</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="#5B8C3F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2.2"/><circle cx="6" cy="18" r="2.2"/><circle cx="18" cy="12" r="2.2"/><path d="M8 6h3a4 4 0 0 1 4 4M8 18h3a4 4 0 0 0 4-4"/></svg>
          </div>
          <div class="nlabel">Test</div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ HOW IT WORKS ============ -->
<section class="product" id="platform">
  <div class="wrap">
    <div class="fhead" style="margin-bottom:58px">
      <span class="eyebrow">How it works</span>
      <h2 class="section-title">Four steps, run on a loop.</h2>
    </div>
    <div class="steps">

      <!-- 1 STRATEGY -->
      <div class="step-row">
        <div class="step-copy">
          <div class="step s1"><span class="badge">1</span><span class="kicker">Strategy</span></div>
          <h3>Set a goal.</h3>
          <p class="desc">You decide what will move the floor this cycle, a goal, a target, a window. Bespoke turns it into a measurable experiment your whole team can run.</p>
        </div>
        <div class="step-visual">
          <div class="browser">
            <div class="bar"><i></i><i></i><i></i></div>
            <div class="form">
              <div class="frow"><span class="k">Goal</span><span class="v"><span class="chip ink">Increase dessert attach</span></span></div>
              <div class="frow"><span class="k">Target lift</span><span class="v">+8 pts attach rate</span></div>
              <div class="frow"><span class="k">Window</span><span class="v">Jun 1 to Jun 30</span></div>
              <div class="frow"><span class="k">Applies to</span><span class="v"><span class="chip red">4 locations &middot; dinner</span></span></div>
              <div class="actions"><span class="mini-btn solid">Launch experiment</span><span class="mini-btn soft">Save draft</span></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 2 TRAIN -->
      <div class="step-row reverse">
        <div class="step-copy">
          <div class="step s2"><span class="badge">2</span><span class="kicker">Train</span></div>
          <h3>Coach the team like a game.</h3>
          <p class="desc">Short, game-like lessons built from your own playbook. Your team grows their tips and their craft in an app that feels like play.</p>
        </div>
        <div class="step-visual">
          <div class="phone">
            <div class="phone-screen">
              <div class="ps-top">
                <span class="ps-pill"><svg viewBox="0 0 24 24" fill="#D6453C"><path d="M12 23a7 7 0 0 0 7-7c0-4-3-6-4-9-1 1.5-1.5 3-3 4 0-2.5 0-4.5-2-6-.5 4-4 5.5-4 11a6 6 0 0 0 6 7z"/></svg> 5</span>
                <span class="ps-pill"><svg viewBox="0 0 24 24" fill="#E0A33C"><path d="M13 2L4 14h6l-1 8 9-12h-6z"/></svg> 420</span>
              </div>
              <div class="ps-lesson">
                <div class="lh"><span class="tag">Today's lesson</span><span class="min">~10 min</span></div>
                <div class="lr">
                  <svg class="ps-can" viewBox="0 0 24 34" fill="none"><rect x="4" y="5" width="16" height="27" rx="3.5" fill="#ffffff" opacity=".92"/><rect x="8" y="2" width="8" height="4" rx="1.5" fill="#ffffff" opacity=".7"/><rect x="6.5" y="13" width="11" height="6" rx="1" fill="#D5942B" opacity=".5"/></svg>
                  <div>
                    <div class="lnum">Lesson 2</div>
                    <div class="ltitle">The Beverage Ask</div>
                  </div>
                </div>
              </div>
              <div class="ps-acts">
                <div class="ps-act">
                  <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8 8 0 0 1-11.5 7.2L3 21l2.3-6.5A8 8 0 1 1 21 11.5z"/></svg></span>
                  <span class="nm">Multiple Choice</span><span class="start">START</span>
                </div>
                <div class="ps-act dim">
                  <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg></span>
                  <span class="nm">Rehearsal</span><span class="circ"></span>
                </div>
                <div class="ps-act dim">
                  <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6h12M12 6v12"/></svg></span>
                  <span class="nm">Fill in the Blank</span><span class="circ"></span>
                </div>
                <div class="ps-act dim">
                  <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8 8 0 0 1-11.5 7.2L3 21l2.3-6.5A8 8 0 1 1 21 11.5z"/></svg></span>
                  <span class="nm">Multiple Choice</span><span class="circ"></span>
                </div>
                <div class="ps-prog"><span class="t">0 of 4 activities complete</span><span class="p">0%</span></div>
                <div class="ps-barwrap"><span></span></div>
                <div class="ps-nav">
                  <span class="ni on"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2V5zM18 17H6"/></svg>Learn</span>
                  <span class="rec"><span></span></span>
                  <span class="ni"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="3.4"/><path d="M5 21a7 7 0 0 1 14 0"/></svg>Home</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 3 MEASURE -->
      <div class="step-row">
        <div class="step-copy">
          <div class="step s3"><span class="badge">3</span><span class="kicker">Measure</span></div>
          <h3>Measure live service.</h3>
          <p class="desc">Every person on your floor, every behavior, scored against the goal from what actually happened, then handed back as a per-shift scorecard.</p>
        </div>
        <div class="step-visual">
          <div class="browser">
            <div class="bar"><i></i><i></i><i></i></div>
            <img class="screen" src="/marketing/assets/app/dashboard_named.png" alt="Server scorecard dashboard">
          </div>
          <div class="bars-panel">
            <div class="brow"><span class="bn">Greeting</span><span class="bar"><span class="b-green" style="width:88%"></span></span><span class="bv green">strong</span></div>
            <div class="brow"><span class="bn">Dessert attach</span><span class="bar"><span class="b-amber" style="width:62%"></span></span><span class="bv amber">improving</span></div>
            <div class="brow"><span class="bn">Opening line</span><span class="bar"><span class="b-red" style="width:30%"></span></span><span class="bv red">coach this</span></div>
          </div>
        </div>
      </div>

      <!-- 4 TEST -->
      <div class="step-row reverse">
        <div class="step-copy">
          <div class="step s4"><span class="badge">4</span><span class="kicker">Test</span></div>
          <h3>Prove what actually worked.</h3>
          <p class="desc">Bespoke sets up control and test locations, then measures them against each other and against your own prior periods. For the first time, world-class analytics works out of the box, so you know what truly drives revenue before you roll it out everywhere.</p>
        </div>
        <div class="step-visual">
          <div class="abtest">
            <div class="ab-head">
              <span class="t">Result &middot; dessert attach</span>
              <span class="verdict"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l5 5L20 6"/></svg> Confirmed</span>
            </div>
            <div class="ab-plot">
              <div class="ab-col a"><span class="val">18.4%</span><div class="colbar" style="height:84px"></div></div>
              <div class="ab-col b"><span class="val">26.5%</span><div class="colbar" style="height:122px"></div></div>
            </div>
            <div class="ab-names"><span>Control</span><span>Coached</span></div>
            <div class="ab-base">
              <span class="lift">Lift <b>+8.1 pts</b> attach rate</span>
              <span class="conf">94% confidence</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>

<!-- ============ FEATURES ============ -->
<section class="features" id="features">
  <div class="wrap">
    <div class="fhead">
      <span class="eyebrow">Features</span>
      <h2 class="section-title">Everything the loop needs.</h2>
      <p class="section-lead">One platform that turns the live conversation on your floor into revenue, organized around the loop. Set the strategy, train the team, then measure and test what works.</p>
    </div>

    <!-- STRATEGY group -->
    <div class="feat-group">
      <div class="grp-head"><span class="grp-badge s1">Strategy</span><span class="grp-sub">Set the play, and see what leadership needs to see.</span></div>
      <div class="feat-grid">
        <article class="feat fc1">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10c.8.8 1 1.5 1 2h6c0-.5.2-1.2 1-2a6 6 0 0 0-4-10z"/></svg></div>
          <h3>Strategic insights</h3>
          <p>Clear recommendations on where to grow revenue next, with the data to back every one.</p>
        </article>
        <article class="feat fc1">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2V5zM18 17H6"/></svg></div>
          <h3>Custom curriculums</h3>
          <p>Build the exact playbook your strategy calls for, shaped to your menu, your team, and your goals.</p>
        </article>
        <article class="feat fc1">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg></div>
          <h3>Complete visibility</h3>
          <p>See every behavior on your floor and exactly how it ties to revenue.</p>
        </article>
        <article class="feat fc1">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-7-6-7-11a7 7 0 0 1 14 0c0 5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/></svg></div>
          <h3>Location-by-location reporting</h3>
          <p>Compare performance across every location, reported the way leadership needs to see it.</p>
        </article>
        <article class="feat fc1">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="8" height="8" rx="1.5"/><rect x="13" y="3" width="8" height="5" rx="1.5"/><rect x="13" y="11" width="8" height="10" rx="1.5"/><rect x="3" y="14" width="8" height="7" rx="1.5"/></svg></div>
          <h3>Manager dashboards</h3>
          <p>One place for leadership to see every location, every behavior, and every dollar.</p>
        </article>
        <article class="feat fc1">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.4"/></svg></div>
          <h3>Goal and target tracking</h3>
          <p>Track progress toward every revenue target as it happens, by location and by team.</p>
        </article>
      </div>
    </div>

    <!-- TRAINING group -->
    <div class="feat-group">
      <div class="grp-head"><span class="grp-badge s2">Training</span><span class="grp-sub">Level up the team, shift after shift.</span></div>
      <div class="feat-grid">
        <article class="feat fc2">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="5"/><path d="M6 12h4M8 10v4"/><circle cx="15.5" cy="11" r="1"/><circle cx="18" cy="13.5" r="1"/></svg></div>
          <h3>Gamified learning</h3>
          <p>Training that feels like a game, so your team actually shows up and sharpens their craft.</p>
        </article>
        <article class="feat fc2">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg></div>
          <h3>Live rehearsal</h3>
          <p>Staff practice the moment before service, so the real conversation lands.</p>
        </article>
        <article class="feat fc2">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="13" width="5" height="8" rx="1"/><rect x="9.5" y="8" width="5" height="13" rx="1"/><rect x="16" y="16" width="5" height="5" rx="1"/></svg></div>
          <h3>Leaderboards</h3>
          <p>Friendly competition that keeps top performers visible and pulls everyone up.</p>
        </article>
        <article class="feat fc2">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4zM7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3"/></svg></div>
          <h3>Automated incentives</h3>
          <p>Contests and rewards that fire on their own, tied to real sales behavior and outcomes.</p>
        </article>
        <article class="feat fc2">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8 8 0 0 1-11.5 7.2L3 21l2.3-6.5A8 8 0 1 1 21 11.5z"/></svg></div>
          <h3>Coaching and feedback</h3>
          <p>Specific, personal guidance that turns a weak habit into a winning one.</p>
        </article>
        <article class="feat fc2">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"/></svg></div>
          <h3>Pre-shift notifications</h3>
          <p>The right focus, top of mind, the moment before the floor opens.</p>
        </article>
      </div>
    </div>

    <!-- MEASUREMENT group -->
    <div class="feat-group">
      <div class="grp-head"><span class="grp-badge s3">Measurement &amp; Testing</span><span class="grp-sub">Prove what works with real data.</span></div>
      <div class="feat-grid">
        <article class="feat fc3">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2.2"/><circle cx="6" cy="18" r="2.2"/><circle cx="18" cy="12" r="2.2"/><path d="M8 6h3a4 4 0 0 1 4 4M8 18h3a4 4 0 0 0 4-4"/></svg></div>
          <h3>Statistically significant testing</h3>
          <p>Test every change with the statistical rigor to tell a real lift from random noise.</p>
        </article>
        <article class="feat fc3">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h8M16 6h4M4 12h4M12 12h8M4 18h10M18 18h2"/><circle cx="14" cy="6" r="2"/><circle cx="10" cy="12" r="2"/><circle cx="16" cy="18" r="2"/></svg></div>
          <h3>Control and test setup</h3>
          <p>Set your control and test locations, choose the window, and run a clean experiment.</p>
        </article>
        <article class="feat fc3">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h4l2.5-7 5 14 2.5-7H21"/></svg></div>
          <h3>Anomaly detection</h3>
          <p>The platform surfaces the outliers worth chasing, so you catch the problem and the opportunity early.</p>
        </article>
        <article class="feat fc3">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4"/></svg></div>
          <h3>POS integration</h3>
          <p>Connect your point of sale so every behavior is matched to the dollars it earned.</p>
        </article>
        <article class="feat fc3">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4"/></svg></div>
          <h3>Mics for any room</h3>
          <p>Wearable, wired, and fixed-station mics that capture clean audio in the noisiest rooms.</p>
        </article>
        <article class="feat fc3">
          <div class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z"/><path d="M9 12l2 2 4-4"/></svg></div>
          <h3>Privacy shield</h3>
          <p>Recordings and transcripts stay private from management, which keeps the team's trust and buy-in.</p>
        </article>
      </div>
    </div>
  </div>
</section>

<!-- ============ TEAM ============ -->
<section class="team" id="team">
  <div class="wrap">
    <div class="head">
      <span class="eyebrow center">Team</span>
      <h2 class="section-title">The people behind Bespoke.</h2>
      <p class="section-lead">Operators and engineers who have built and scaled, in real restaurants and at institutional scale.</p>
    </div>

    <p class="subhead">Founders</p>
    <div class="founders">
      <article class="fcard">
        <img class="pic" src="/marketing/assets/team/eric.png" alt="Eric Chung">
        <div class="fbody">
          <h3>Eric Chung</h3>
          <div class="role">Founder &amp; CEO</div>
          <p>Former COO of Coinbase Custody, where he built operations from pre-launch to the largest crypto custodian in the world. Now applying that institutional rigor to the in-person economy.</p>
          <span class="cred"><img src="/marketing/assets/logos/coinbase.png" alt="Coinbase"></span>
        </div>
      </article>
      <article class="fcard">
        <img class="pic" src="/marketing/assets/team/preston.png" alt="Preston Rohner">
        <div class="fbody">
          <h3>Preston Rohner</h3>
          <div class="role">Founding Engineer</div>
          <p>More than eight years in restaurant technology. Principal Engineer at Ordermark, a SoftBank-backed company, where he built systems processing hundreds of thousands of daily transactions.</p>
          <span class="cred"><img src="/marketing/assets/logos/ordermark.png" alt="Ordermark"></span>
        </div>
      </article>
    </div>

    <p class="subhead">Advisors</p>
    <div class="advisors">
      <article class="acard">
        <img class="pic" src="/marketing/assets/team/barzelay.png" alt="David Barzelay">
        <h3>David Barzelay</h3>
        <div class="role">Advisor</div>
        <p>Founder and Chef of Lazy Bear, two Michelin stars. James Beard Award semifinalist.</p>
      </article>
      <article class="acard">
        <img class="pic" src="/marketing/assets/team/canter.png" alt="Alex Canter">
        <h3>Alex Canter</h3>
        <div class="role">Advisor</div>
        <p>CEO and co-founder of Ordermark and Nextbite, both acquired. Forbes 30 Under 30. Fourth-generation restaurateur at Canter's Deli.</p>
      </article>
      <article class="acard">
        <img class="pic" src="/marketing/assets/team/lew.png" alt="Justin Lew">
        <h3>Justin Lew</h3>
        <div class="role">Advisor</div>
        <p>Co-founder of Horsefeather and Last Rites. San Francisco hospitality veteran and high-margin beverage expert.</p>
      </article>
    </div>
  </div>
</section>

`;
